import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Loader2, Play, RotateCcw, Square } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  fetchGreetingAudio,
  useGreetingStatus,
  usePrepareGreeting,
} from '@/api/aiAgents'

interface Props {
  companyId: number | undefined
  agentId: number
  /** The greeting text was edited in the form and is not saved yet. */
  textChanged: boolean
  /** The voice was changed in the form and is not saved yet. */
  voiceChanged: boolean
}

// Engines whose voice is the one picked in this form (GPT-Live and OpenAI keep theirs elsewhere).
const FORM_VOICE_ENGINES = ['elevenlabs', 'deepgram']

/**
 * "Listen to the greeting", under the greeting field of the agent editor.
 *
 * The server prepares the greeting when the agent is saved, never on each
 * keystroke: for GPT-Live agents it is the very file played on pickup, for the
 * other engines a preview in the same voice. The audio is fetched as soon as it
 * is ready, so the click plays it at once (Safari refuses to play after a wait).
 *
 * Phone: one full-width button, 44 px tall. Computer: a compact button inline.
 */
export function GreetingPreview({ companyId, agentId, textChanged, voiceChanged }: Props) {
  const { t } = useTranslation()
  const { data: status } = useGreetingStatus(companyId, agentId)
  const prepare = usePrepareGreeting(companyId, agentId)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [playing, setPlaying] = useState(false)
  const [waitingForAudio, setWaitingForAudio] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const ready = status?.etat === 'pret'
  const version = status?.version

  // Download the ready greeting (a few dozen KB) once per version; free it when it changes.
  useEffect(() => {
    if (!ready || !companyId) return
    let cancelled = false
    let url: string | null = null
    fetchGreetingAudio(companyId, agentId)
      .then((blob) => {
        if (cancelled) return
        url = URL.createObjectURL(blob)
        setAudioUrl(url)
      })
      .catch(() => {
        if (!cancelled) setAudioUrl(null)
      })
    return () => {
      cancelled = true
      audioRef.current?.pause()
      setPlaying(false)
      setAudioUrl(null)
      if (url) URL.revokeObjectURL(url)
    }
  }, [ready, version, companyId, agentId])

  useEffect(() => () => audioRef.current?.pause(), [])

  const start = (url: string) => {
    const audio = audioRef.current ?? new Audio()
    audioRef.current = audio
    audio.src = url
    audio.onended = () => setPlaying(false)
    audio
      .play()
      .then(() => setPlaying(true))
      .catch(() => {
        setPlaying(false)
        toast.error(t('manager.aiAgents.greetingPreview.playError'))
      })
  }

  // Clicked before the download finished: play as soon as it lands.
  useEffect(() => {
    if (waitingForAudio && audioUrl) {
      setWaitingForAudio(false)
      start(audioUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingForAudio, audioUrl])

  if (!status || !agentId) return null

  const hint = (text: string) => (
    <p className="mt-3 text-xs text-gray-500 dark:text-slate-400">{text}</p>
  )

  if (status.etat === 'aucun') {
    return status.raison === 'variable' ? hint(t('manager.aiAgents.greetingPreview.variable')) : null
  }
  const voiceMatters = FORM_VOICE_ENGINES.includes(status.moteur ?? '')
  if (textChanged || (voiceChanged && voiceMatters)) {
    return hint(t('manager.aiAgents.greetingPreview.saveFirst'))
  }

  const startPreparation = () =>
    prepare.mutate(undefined, {
      onError: () => toast.error(t('manager.aiAgents.greetingPreview.failed')),
    })

  const onListen = () => {
    if (status.etat === 'absent') {
      startPreparation()
      return
    }
    if (playing) {
      audioRef.current?.pause()
      if (audioRef.current) audioRef.current.currentTime = 0
      setPlaying(false)
      return
    }
    if (audioUrl) start(audioUrl)
    else setWaitingForAudio(true)
  }

  const preparing = status.etat === 'preparation' || prepare.isPending
  const buttonClass = cn(
    'h-11 w-full text-sm sm:h-8 sm:w-auto',
    'text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900',
    'hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:text-blue-800',
  )

  if (status.etat === 'echec' && !preparing) {
    return (
      <div
        className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
        aria-live="polite"
      >
        <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
          {t('manager.aiAgents.greetingPreview.failed')}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={startPreparation}
          className={buttonClass}
        >
          <RotateCcw className="h-4 w-4" aria-hidden />
          {t('manager.aiAgents.greetingPreview.retry')}
        </Button>
      </div>
    )
  }

  let label: string
  if (preparing) label = t('manager.aiAgents.greetingPreview.preparing')
  else if (playing) label = t('manager.aiAgents.greetingPreview.stop')
  else label = t('manager.aiAgents.greetingPreview.listen')

  const busy = preparing || waitingForAudio
  return (
    <div className="mt-3" aria-live="polite">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={preparing}
        aria-busy={busy}
        onClick={onListen}
        className={buttonClass}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : playing ? (
          <Square className="h-3.5 w-3.5 fill-current" aria-hidden />
        ) : (
          <Play className="h-4 w-4 fill-current" aria-hidden />
        )}
        {label}
      </Button>
    </div>
  )
}
